import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Globe, MapPin, Monitor, Clock, Hash } from 'lucide-react';

type SessionRecord = {
  id: string;
  session_id: string;
  min_value: number;
  max_value: number;
  count: number;
  started_at: string;
  ended_at: string | null;
  path: string | null;
  referrer: string | null;
  user_agent: string | null;
  platform: string | null;
  street_address: string | null;
  city: string | null;
  country: string | null;
  region: string | null;
  created_at: string;
};

export const SessionHistory = () => {
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('session_history')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching session history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getPlatformColor = (platform: string | null) => {
    switch (platform?.toLowerCase()) {
      case 'android':
        return 'bg-green-500/10 text-green-500';
      case 'ios':
        return 'bg-blue-500/10 text-blue-500';
      case 'windows':
        return 'bg-cyan-500/10 text-cyan-500';
      case 'mac':
        return 'bg-purple-500/10 text-purple-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Session History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading session history...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Session History ({sessions.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          {sessions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No session history yet
            </p>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <Card key={session.id} className="bg-muted/50">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Hash className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <code className="text-sm font-mono truncate">
                              {session.session_id}
                            </code>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {formatDate(session.started_at)}
                          </div>
                        </div>
                        {session.platform && (
                          <Badge
                            variant="secondary"
                            className={getPlatformColor(session.platform)}
                          >
                            <Monitor className="w-3 h-3 mr-1" />
                            {session.platform}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-medium">
                          Range: {session.min_value} - {session.max_value}
                        </span>
                        <span className="text-muted-foreground">
                          Count: {session.count}
                        </span>
                      </div>

                      {session.street_address && (
                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="font-medium text-foreground">
                            {session.street_address}
                          </span>
                        </div>
                      )}

                      {(session.city || session.country || session.region) && (
                        <div className="flex items-start gap-2 text-sm">
                          <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">
                            {[session.city, session.region, session.country]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </div>
                      )}

                      {session.path && (
                        <div className="flex items-start gap-2 text-sm">
                          <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <code className="text-xs text-muted-foreground truncate">
                            {session.path}
                          </code>
                        </div>
                      )}

                      {session.referrer && session.referrer !== 'direct' && (
                        <div className="text-xs text-muted-foreground truncate">
                          Referrer: {session.referrer}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
